import React, { useState, useEffect } from 'react';
import { Typography, Card, Table, Button, Space, Tag, Input, Row, Col, App } from 'antd';
import { SearchOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const IndustryDocuments = () => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const loadDocuments = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page,
        page_size: pageSize,
        search: searchText,
        category: categoryFilter,
      });

      const response = await fetch(`${API_BASE_URL}/industry/documents?${params}`);
      
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.data);
        setPagination({
          current: page,
          pageSize: pageSize,
          total: data.total,
        });
      } else {
        message.error('加载规范文档数据失败');
      }
    } catch (error) {
      console.error('加载规范文档数据失败:', error);
      message.error('加载规范文档数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocuments();
  }, [searchText, categoryFilter]);

  const getCategoryTag = (category) => {
    const colors = {
      '设计规范': 'blue',
      '验收规范': 'green',
      '施工规范': 'orange',
      '技术指南': 'purple',
    };
    return <Tag color={colors[category] || 'default'}>{category}</Tag>;
  };

  const getTypeTag = (type) => {
    const colors = {
      'PDF': 'red',
      'Word': 'blue',
      'Excel': 'green',
    };
    return <Tag color={colors[type] || 'default'}>{type}</Tag>;
  };

  const columns = [
    {
      title: '文档标题',
      dataIndex: 'title',
      key: 'title',
      width: '35%',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: '12%',
      render: (category) => getCategoryTag(category),
    },
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      width: '10%',
      render: (type) => getTypeTag(type),
    },
    {
      title: '文件大小',
      dataIndex: 'file_size',
      key: 'file_size',
      width: '13%',
      render: (size) => size ? `${(size / 1024 / 1024).toFixed(2)} MB` : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: '25%',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EyeOutlined />} size="small">
            预览
          </Button>
          <Button type="primary" icon={<DownloadOutlined />} size="small">
            下载
          </Button>
        </Space>
      ),
    },
  ];

  const handleTableChange = (pagination) => {
    loadDocuments(pagination.current, pagination.pageSize);
  };

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>规范文档</Title>
        <Text type="secondary">技术文档和指导手册库</Text>
      </div>

      <Card style={{ marginBottom: '24px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="搜索文档标题"
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Input
              placeholder="按分类筛选"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              allowClear
            />
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={documents}
          loading={loading}
          rowKey="id"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 个文档`,
          }}
          onChange={handleTableChange}
        />
      </Card>
    </div>
  );
};

export default IndustryDocuments;
